import * as _ from "lodash";
import { evaluate } from "mathjs";

const BR_NUMBER_REGEXP = /^[+-]?(\d{1,3}([.,]\d{3})*|\d+)([.,]\d+)?$/;
const FORMULA_VARIABLE_REGEXP = /[a-zA-Z_][\w.]*/g;

export function getFieldScore(key: string, fields: any[], currentValues: any[], allowIgnoreScores = false) {
  const field = fields.find((field) => field.chave === key);
  if (!field) {
    return undefined;
  }
  if (!allowIgnoreScores && field.IgnoreScore) {
    return 0
  }

  if (field.component === "Multiplaescolha" && field.valores) {
    if (field.PermitirMultiplasSelecoes) {
      const value = currentValues[field.chave];
      if (!value) {
        return undefined;
      }
      let score = 0
      
      for (const selectedField of value) {
        const selectionFieldFound = field.valores.find(
          (fieldValue: any) =>
            fieldValue.nome
              .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
              .trim() ===
            selectedField.name
              .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
              .trim()
        );
      if (selectionFieldFound && !isNaN(Number(selectionFieldFound.score))) {
          score += parseFloat(selectionFieldFound.score);
        }
      }
      return score;
    }
  }

  if (field.valores) {
    const valueFound = field.valores.find(
      (value: any) =>
        value.nome.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "").trim() ===
        currentValues[field.chave]
          .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "")
          .trim()
    );

    if (valueFound && valueFound.score && !isNaN(Number(valueFound.score))) {
      return parseFloat(valueFound.score);
    }
  }
  return undefined;
}

export async function calculateFormScoresBySections(
  currentValues: any,
  availableForms: any[]
) {
  const formScores: any = {};
  if (!currentValues || !availableForms) {
    return formScores;
  }

  const keys = _.keys(currentValues);

  const allFields = availableForms.map((form) => form.campos).flat();

  const scoreFieldsWithFormula = allFields.filter(
    (field) => field.component === "ScoreCalculator" && !!field.Formula
  );
  const currentScores = keys.reduce((scores: any, key) => {
    const fieldScore = getFieldScore(key, allFields, currentValues);
    if (typeof fieldScore !== "undefined") {
      scores[key.replace(/\./g, "_")] = fieldScore;
    }
    return scores;
  }, {});
  for (const scoreComponent of scoreFieldsWithFormula) {
    const fieldValue = calculateFieldFormula(
      scoreComponent,
      scoreFieldsWithFormula,
      currentScores
    );
    if (typeof fieldValue !== 'undefined') {
      formScores[scoreComponent.chave] = parseFloat(fieldValue).toFixed(2);
      currentScores[scoreComponent.chave.replace(/\./g, "_")] = fieldValue;
    }
  }
  return formScores;
}

export function calculateFieldsFormulas(fields: any[], currentValues: any[]) {
  const valuesToCheck: any = {};

  const calculationFields = fields.filter(
    (campo) =>
      campo.component === "CalculoAutomatico" && campo.Formula !== "tipologias"
  );

  // Typology fields are calculated different since it is a custom component
  const typologyFields = fields.filter(
    (campo) =>
      campo.component === "CalculoAutomatico" && campo.Formula === "tipologias"
  );
  for (const field of typologyFields) {
    const typologyField = fields.find(
      (campo) => campo.component === "Tipologias"
    );
    const typologies = currentValues[typologyField?.chave];
    if (!typologies) {
      continue;
    }
    const typologiesTotal = typologies.reduce((total: number, typology: any) => {
      const tipologyLength = typology?.tipologyLength;
      const quantity = typology?.quantity;
      const calc = parseFloat(tipologyLength?.value) * parseFloat(quantity);
      total += calc;
      return total;
    }, 0);
    currentValues[field.chave] = typologiesTotal;
  }

  for (const key of _.keys(currentValues)) {
    // Format keys to match mathjs syntax
    const keyFormatted = key.replace(/\./g, "_");
    const value = _.get(currentValues, key);
    if (!isNaN(value)) {
      valuesToCheck[keyFormatted] = value;
    }
    if (typeof value === "string" && BR_NUMBER_REGEXP.test(value)) {
      valuesToCheck[keyFormatted] = parseFloat(value.replace(/\,/g, "."));
    }

    const fieldFound = fields.find(field => field.chave === key)
    if (fieldFound && fieldFound.component === 'Multiplaescolha') {
      valuesToCheck[keyFormatted] = getFieldScore(fieldFound.chave, fields, currentValues)
    }
  }

  for (const field of calculationFields) {
    const variablesToEvaluate = String(field.Formula).match(
      FORMULA_VARIABLE_REGEXP
    );
    if (!variablesToEvaluate) {
      continue;
    }
    
    const fieldValue = calculateFieldFormula(field, calculationFields, {
      ...valuesToCheck,
    });
    if (fieldValue) {
      currentValues[field.chave] = fieldValue;
      valuesToCheck[field.chave.replace(/\./g, "_")] = fieldValue;
    }
  }
}

function calculateFieldFormula(field: any, calculationFields: any[], currentValues: any) {
  const variablesToEvaluate = String(field.Formula).match(
    FORMULA_VARIABLE_REGEXP
  );
  if (!variablesToEvaluate) {
    return undefined;
  }

  try {
    variablesToEvaluate.forEach((variable) => {
      if (!currentValues[variable]) {
        const nestedField = calculationFields.find((f) => f.chave === variable);
        if (nestedField) {
          const nestedResult = calculateFieldFormula(
            nestedField,
            calculationFields,
            currentValues
          );
          if (typeof nestedResult !== undefined) {
            currentValues[variable.replace(/\./g, "_")] = nestedResult;
          }
        }
      }
    });

    if (field.IgnoreEmpty) {
      const valuesNotFound = variablesToEvaluate.filter(
        (key) => !currentValues[key.replace(/\./g, "_")]
      );
      valuesNotFound.forEach((key) => {
        currentValues[key.replace(/\./g, "_")] = 0;
      });
    }

    // Replace all . with _ for mathjs evaluation
    const formulaForEvaluation = field.Formula.replace(/\./g, "_");

    // Evaluate the formula using mathjs
    let result = evaluate(formulaForEvaluation, currentValues);
    if (typeof result !== undefined) {
      if (field.DecimalCases && !isNaN(Number(field.DecimalCases))) {
        result = parseFloat(result.toFixed(Number(field.DecimalCases)));
      }
      currentValues[field.chave] = result;
    }
    return result;
  } catch (error) {
    // console.log("Error calculating formula", error);
    return undefined;
  }
}
